import React, { useState, useRef, useEffect } from 'react';
import Header from '../components/Header';
import { useI18n } from '../i18n';

const CONTACT_ENDPOINT = '/api/contact';
const LINKEDIN_URL = 'https://www.linkedin.com/company/faus-ai';

const SHOW_TRUSTED_SCHOOLS = true;

const trustedSchools = [
  {
    name: 'San Esteban',
    logo: '/school-logos/san-esteban.png',
    url: 'https://colegiosanesteban.edu.ar/',
  },
  {
    name: 'Nightingale School',
    logo: '/school-logos/nightingale.png',
    url: 'https://nightingaleschool.edu.ar/',
  },
  {
    name: 'Godspell College',
    logo: '/school-logos/godspell.png',
    url: 'https://www.godspellcollege.com.ar/',
  },
  {
    name: 'Arrayanes',
    logo: '/school-logos/arrayanes.png',
    url: 'https://www.colegioarrayanes.edu.ar/',
  },
];

const TESTIMONIAL_KEYS = ['item1', 'item2', 'item3', 'item4', 'item5'];

const teamMembers = [
  { key: 'mateo', photo: '/team/mateo.jpg' },
  { key: 'tomas', photo: '/team/tomas.jpg' },
];

async function submitLandingContact(payload) {
  const response = await fetch(CONTACT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  let responseBody = null;
  try {
    responseBody = await response.json();
  } catch (_error) {
    responseBody = null;
  }

  if (!response.ok) {
    throw new Error(responseBody?.error || 'contact-submit-failed');
  }

  return responseBody;
}

// Card data for horizontal scrolling section
const cardIcons = {
  management: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  support: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ),
  literacy: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  ),
  evidence: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
};

function FeatureCard({ cardKey, title, description, index }) {
  return (
    <div 
      className="flex-shrink-0 w-[280px] md:w-[320px] bg-white rounded-3xl p-8 border border-black/10 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="w-14 h-14 bg-black text-white rounded-2xl flex items-center justify-center mb-6 shadow-sm">
        {cardIcons[cardKey]}
      </div>
      <h3 className="text-xl font-bold mb-3 text-black tracking-tight">{title}</h3>
      <p className="text-neutral-600 leading-relaxed text-[15px]">{description}</p>
    </div>
  );
}

function HorizontalScrollCards({ children, tone = 'light' }) {
  const fadeFrom = tone === 'dark' ? 'from-black' : 'from-white';
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = (scrollRef.current.firstElementChild?.offsetWidth ?? 316) + 24;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="relative w-full">
      {/* Scroll buttons */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white rounded-full border border-black/10 shadow-lg text-black hover:bg-neutral-50 transition-all flex items-center justify-center -ml-3 md:ml-0"
          aria-label="Scroll left"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white rounded-full border border-black/10 shadow-lg text-black hover:bg-neutral-50 transition-all flex items-center justify-center -mr-3 md:mr-0"
          aria-label="Scroll right"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
      
      {/* Gradient fades */}
      <div className={`absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r ${fadeFrom} to-transparent pointer-events-none z-[1]`} style={{ opacity: canScrollLeft ? 1 : 0 }} />
      <div className={`absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l ${fadeFrom} to-transparent pointer-events-none z-[1]`} style={{ opacity: canScrollRight ? 1 : 0 }} />
      
      {/* Scrollable container */}
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-6 overflow-x-auto scrollbar-hide py-4 px-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {children}
      </div>
    </div>
  );
}

function LinkedInIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6.9 8.9H3.8v10.3h3.1V8.9zM5.4 7.5a1.8 1.8 0 100-3.6 1.8 1.8 0 000 3.6zM20.2 13.6c0-3.1-1.7-4.6-3.9-4.6-1.8 0-2.6 1-3.1 1.7V8.9h-3.1c.1 1 0 10.3 0 10.3h3.1v-5.7c0-.3 0-.6.1-.8.3-.6.8-1.2 1.8-1.2 1.3 0 1.8 1 1.8 2.4v5.3h3.1v-5.6z" />
    </svg>
  );
}

function TeacherContactModal({ isOpen, onClose, t, locale }) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setIsSubmitted(false);
    setIsSubmitting(false);
    setSubmitError('');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    setIsSubmitting(true);
    setSubmitError('');

    try {
      await submitLandingContact({
        formType: 'teacher',
        source: 'teacher-modal',
        locale,
        page: window.location.pathname,
        firstName: String(formData.get('firstName') || ''),
        lastName: String(formData.get('lastName') || ''),
        institution: String(formData.get('institution') || ''),
        email: String(formData.get('email') || ''),
        message: String(formData.get('message') || ''),
        website: String(formData.get('website') || ''),
      });
      form.reset();
      setIsSubmitted(true);
    } catch (error) {
      console.error('Failed to submit teacher contact form', error);
      setSubmitError(t('contact.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl relative border border-black/10">
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 text-neutral-400 hover:text-black transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-8 md:p-12">
          <h2 className="text-3xl font-bold text-black mb-2">{t('contact.teacherTitle')}</h2>
          <p className="text-neutral-600 mb-6 leading-relaxed text-sm md:text-base">{t('contact.description')}</p>

          <a
            href="/app/"
            className="inline-block text-sm font-semibold text-black underline hover:no-underline mb-8"
          >
            {t('contact.alreadyHaveAccount')}
          </a>

          {isSubmitted ? (
            <div className="text-center py-12">
              <h3 className="text-2xl font-bold text-black mb-3">{t('contact.messageSent.title')}</h3>
              <p className="mb-6 text-neutral-600 max-w-md mx-auto">
                {t('contact.messageSent.description')}
              </p>
              <button 
                onClick={onClose}
                className="bg-black text-white text-xs font-bold tracking-widest px-8 py-4 rounded-full hover:bg-neutral-800 uppercase transition-all"
              >
                {t('common.close')}
              </button>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="hidden" aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col space-y-2">
                  <label htmlFor="modal-firstName" className="text-xs font-bold uppercase tracking-widest text-black">
                    {t('common.firstName')} <span className="text-neutral-400 font-normal">({t('common.required')})</span>
                  </label>
                  <input
                    type="text"
                    id="modal-firstName"
                    name="firstName"
                    required
                    className="bg-neutral-50 border border-black/15 rounded-2xl px-4 py-3 text-black focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black transition-all text-sm"
                  />
                </div>
                <div className="flex flex-col space-y-2">
                  <label htmlFor="modal-lastName" className="text-xs font-bold uppercase tracking-widest text-black">
                    {t('common.lastName')} <span className="text-neutral-400 font-normal">({t('common.required')})</span>
                  </label>
                  <input
                    type="text"
                    id="modal-lastName"
                    name="lastName"
                    required
                    className="bg-neutral-50 border border-black/15 rounded-2xl px-4 py-3 text-black focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black transition-all text-sm"
                  />
                </div>
              </div>

              <div className="flex flex-col space-y-2">
                <label htmlFor="modal-institution" className="text-xs font-bold uppercase tracking-widest text-black">
                  {t('common.institution')} <span className="text-neutral-400 font-normal">({t('common.required')})</span>
                </label>
                <input
                  type="text"
                  id="modal-institution"
                  name="institution"
                  required
                  className="bg-neutral-50 border border-black/15 rounded-2xl px-4 py-3 text-black focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black transition-all text-sm w-full"
                />
              </div>

              <div className="flex flex-col space-y-2">
                <label htmlFor="modal-email" className="text-xs font-bold uppercase tracking-widest text-black">
                  {t('common.email')} <span className="text-neutral-400 font-normal">({t('common.required')})</span>
                </label>
                <input
                  type="email"
                  id="modal-email"
                  name="email"
                  required
                  className="bg-neutral-50 border border-black/15 rounded-2xl px-4 py-3 text-black focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black transition-all text-sm w-full"
                />
              </div>

              <div className="flex flex-col space-y-2">
                <label htmlFor="modal-message" className="text-xs font-bold uppercase tracking-widest text-black">
                  {t('common.message')} <span className="text-neutral-400 font-normal">({t('common.required')})</span>
                </label>
                <textarea
                  id="modal-message"
                  name="message"
                  required
                  rows={4}
                  className="bg-neutral-50 border border-black/15 rounded-2xl px-4 py-3 text-black focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black transition-all text-sm w-full resize-none"
                ></textarea>
              </div>

              <div className="pt-2">
                {submitError && (
                  <p className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">{submitError}</p>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-black text-white text-xs font-bold tracking-widest px-8 py-4 rounded-full hover:bg-neutral-800 uppercase transition-all disabled:opacity-50"
                >
                  {isSubmitting ? t('common.sending') : t('common.send')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { t, locale } = useI18n();
  const [isTeacherModalOpen, setIsTeacherModalOpen] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    setIsSubmitting(true);
    setSubmitError('');

    try {
      await submitLandingContact({
        formType: 'contact',
        source: 'contact-section',
        locale,
        page: window.location.pathname,
        firstName: String(formData.get('firstName') || ''),
        lastName: String(formData.get('lastName') || ''),
        email: String(formData.get('email') || ''),
        message: String(formData.get('message') || ''),
        website: String(formData.get('website') || ''),
      });
      form.reset();
      setIsSubmitted(true);
    } catch (error) {
      console.error('Failed to submit contact form', error);
      setSubmitError(t('contact.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const heroCards = [
    { key: 'management', title: t('hero.cards.management.title'), description: t('hero.cards.management.description') },
    { key: 'support', title: t('hero.cards.support.title'), description: t('hero.cards.support.description') },
    { key: 'literacy', title: t('hero.cards.literacy.title'), description: t('hero.cards.literacy.description') },
    { key: 'evidence', title: t('hero.cards.evidence.title'), description: t('hero.cards.evidence.description') },
  ];

  const howItWorksSteps = [
    { number: '1', title: t('howItWorks.steps.step1.title'), description: t('howItWorks.steps.step1.description') },
    { number: '2', title: t('howItWorks.steps.step2.title'), description: t('howItWorks.steps.step2.description') },
    { number: '3', title: t('howItWorks.steps.step3.title'), description: t('howItWorks.steps.step3.description') },
    { number: '4', title: t('howItWorks.steps.step4.title'), description: t('howItWorks.steps.step4.description') },
  ];

  return (
    <div className="min-h-screen flex flex-col text-black bg-white selection:bg-black selection:text-white">
      <Header onTeacherClick={() => setIsTeacherModalOpen(true)} />

      <main className="flex-1 flex flex-col items-center">
        {/* Home Section - HERO */}
        <div id="home" className="w-full flex flex-col items-center pt-36 pb-24 px-6 sm:px-8 bg-white">
          {/* Hero Content */}
          <div className="w-full max-w-5xl text-center mb-16">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.08] mb-8 font-bold tracking-tight text-black max-w-4xl mx-auto text-balance">
              {t('hero.title')}
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl font-normal text-neutral-600 max-w-3xl mx-auto leading-relaxed">
              {t('hero.subtitle')}
            </p>
          </div>

          {/* Feature Cards - Horizontal Scroll */}
          <div className="w-full max-w-6xl">
            <HorizontalScrollCards>
              {heroCards.map((card, index) => (
                <FeatureCard
                  key={card.key}
                  cardKey={card.key}
                  title={card.title}
                  description={card.description}
                  index={index}
                />
              ))}
            </HorizontalScrollCards>
          </div>
        </div>

        {/* Vision Section */}
        <div id="vision" className="w-full bg-black text-white px-8 py-24 lg:py-32">
          <div className="w-full max-w-4xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold mb-14 tracking-tight text-white leading-tight">
              {t('vision.title')}
            </h2>

            <div className="space-y-8 text-neutral-300 text-lg lg:text-xl leading-relaxed font-normal">
              <p>{t('vision.paragraph1')}</p>
              <p>{t('vision.paragraph2')}</p>
            </div>
          </div>
        </div>

        {/* Trusted Schools Section */}
        {SHOW_TRUSTED_SCHOOLS && (
        <section className="w-full bg-neutral-50 border-y border-black/5 px-8 py-20 lg:py-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2 className="text-xs font-bold tracking-widest uppercase text-black/50 text-center mb-12">
              {t('trustedSchools.title')}
            </h2>

            <ul className="flex gap-8 overflow-x-auto scrollbar-hide py-2 px-1 md:grid md:grid-cols-4 md:gap-10 md:overflow-visible md:px-0">
              {trustedSchools.map((school) => (
                <li
                  key={school.name}
                  className="flex-shrink-0 w-48 md:w-auto"
                >
                  <a
                    href={school.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${school.name} website`}
                    className="group flex h-24 items-center justify-center px-4 rounded-2xl bg-white border border-black/5 shadow-2xs hover:border-black/20 hover:shadow-xs transition-all duration-300 hover:-translate-y-0.5"
                  >
                    <img
                      src={school.logo}
                      alt={`${school.name} logo`}
                      className="max-h-16 max-w-full object-contain grayscale contrast-125 opacity-70 group-hover:grayscale-0 group-hover:contrast-100 group-hover:opacity-100 group-focus-visible:grayscale-0 group-focus-visible:contrast-100 group-focus-visible:opacity-100 transition-[filter,opacity] duration-300"
                      loading="lazy"
                    />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
        )}

        {/* How It Works Section */}
        <div id="how-it-works" className="w-full bg-white border-b border-black/5 px-8 py-24 lg:py-32">
          <div className="w-full max-w-5xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 tracking-tight text-center text-black">
              {t('howItWorks.title')}
            </h2>
            <p className="text-lg lg:text-xl text-neutral-600 mb-16 text-center max-w-3xl mx-auto leading-relaxed">
              {t('howItWorks.intro')}
            </p>

            {/* Steps Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
              {howItWorksSteps.map((step) => (
                <div 
                  key={step.number}
                  className="bg-neutral-50 rounded-3xl p-8 border border-black/5 shadow-2xs hover:shadow-sm hover:bg-white transition-all"
                >
                  <div className="flex items-start gap-5">
                    <span className="text-4xl font-extrabold text-black/20 leading-none">{step.number}</span>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-black">{step.title}</h3>
                      <p className="text-neutral-600 leading-relaxed text-[15px]">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Demo Videos Showcase */}
            <div className="mb-20">
              <h3 className="text-2xl lg:text-3xl font-bold mb-12 text-center text-black tracking-tight">
                {t('howItWorks.demoSectionTitle')}
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {[
                  { src: '/videos/chat-alumno-demo.mp4', label: t('howItWorks.demoStudent.label'), title: t('howItWorks.demoStudent.title'), description: t('howItWorks.demoStudent.description') },
                  { src: '/videos/docente-reporte-demo.mp4', label: t('howItWorks.demoTeacher.label'), title: t('howItWorks.demoTeacher.title'), description: t('howItWorks.demoTeacher.description') },
                ].map((demo) => (
                  <button
                    key={demo.src}
                    type="button"
                    onClick={() => setPreviewVideo(demo)}
                    className="group text-left bg-neutral-50 rounded-3xl overflow-hidden border border-black/10 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer hover:bg-white"
                  >
                    <div className="relative">
                      <div className="absolute top-4 left-4 z-10 bg-black/80 backdrop-blur-sm text-white text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full">
                        {demo.label}
                      </div>
                      <div className="absolute inset-0 z-[5] flex items-center justify-center bg-black/0 group-hover:bg-black/25 transition-colors duration-300">
                        <div className="w-16 h-16 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-xl opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-300">
                          <svg className="w-7 h-7 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                      <video
                        className="w-full aspect-video object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                      >
                        <source src={demo.src} type="video/mp4" />
                      </video>
                    </div>
                    <div className="p-6">
                      <h4 className="text-lg font-bold text-black mb-2">{demo.title}</h4>
                      <p className="text-neutral-600 text-[15px] leading-relaxed">{demo.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Report Info Box */}
            <div className="bg-black text-white rounded-3xl p-10 lg:p-14 border border-black shadow-xl">
              <h3 className="text-2xl lg:text-3xl font-bold mb-8 text-white tracking-tight">
                {t('howItWorks.reportTitle')}
              </h3>
              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <div className="mt-2 w-2 h-2 bg-white rounded-full flex-shrink-0" />
                  <span className="text-lg text-neutral-300 font-normal">{t('howItWorks.reportItems.item1')}</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="mt-2 w-2 h-2 bg-white rounded-full flex-shrink-0" />
                  <span className="text-lg text-neutral-300 font-normal">{t('howItWorks.reportItems.item2')}</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="mt-2 w-2 h-2 bg-white rounded-full flex-shrink-0" />
                  <span className="text-lg text-neutral-300 font-normal">{t('howItWorks.reportItems.item3')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <section id="testimonials" className="w-full bg-black text-white px-8 py-20 lg:py-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4 tracking-tight text-center text-white">
              {t('testimonials.title')}
            </h2>
            <p className="text-lg lg:text-xl text-neutral-400 mb-10 text-center max-w-3xl mx-auto leading-relaxed">
              {t('testimonials.subtitle')}
            </p>

            <HorizontalScrollCards tone="dark">
              {TESTIMONIAL_KEYS.map((key) => (
                <figure
                  key={key}
                  className="flex-shrink-0 w-[300px] md:w-[380px] flex flex-col bg-white/5 rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-colors duration-300"
                >
                  <svg className="w-8 h-8 text-white/30 mb-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M9.6 6C6.5 7.4 4.5 10.2 4.5 13.6V18h5.7v-5.7H7.4c.1-2 1.3-3.6 3.2-4.6L9.6 6zm9.4 0c-3.1 1.4-5.1 4.2-5.1 7.6V18h5.7v-5.7h-2.8c.1-2 1.3-3.6 3.2-4.6L19 6z" />
                  </svg>
                  <blockquote className="text-base text-white leading-relaxed mb-6">
                    {t(`testimonials.items.${key}.quote`)}
                  </blockquote>
                  <figcaption className="mt-auto">
                    <p className="font-bold text-white">{t(`testimonials.items.${key}.author`)}</p>
                    <p className="text-sm text-neutral-400">{t(`testimonials.items.${key}.role`)}</p>
                  </figcaption>
                </figure>
              ))}
            </HorizontalScrollCards>
          </div>
        </section>

        {/* About Section */}
        <div id="about" className="w-full bg-neutral-50 border-b border-black/5 px-8 py-24 lg:py-32">
          <div className="w-full max-w-4xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold mb-16 text-center tracking-tight text-black">{t('about.title')}</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
              {teamMembers.map((member) => (
                <div key={member.key} className="bg-white rounded-3xl p-8 border border-black/5 shadow-2xs">
                  <img
                    src={member.photo}
                    alt={t(`about.${member.key}.name`)}
                    className="w-28 h-28 rounded-full object-cover mb-6 border border-black/10 shadow-sm"
                    loading="lazy"
                  />
                  <h3 className="text-2xl font-bold text-black mb-4">{t(`about.${member.key}.name`)}</h3>
                  <p className="text-neutral-600 leading-relaxed text-[15px]">
                    {t(`about.${member.key}.bio`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div id="contact" className="w-full bg-white px-8 py-24 lg:py-32">
          <div className="w-full max-w-3xl mx-auto bg-neutral-50 rounded-3xl p-8 md:p-14 border border-black/5 shadow-sm">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-black">{t('contact.title')}</h2>
            <p className="text-neutral-600 text-lg leading-relaxed mb-10">
              {t('contact.description')}
            </p>

            {isSubmitted ? (
              <div className="w-full bg-white rounded-2xl p-10 text-center border border-black/10">
                <h3 className="text-2xl font-bold text-black mb-3">{t('contact.messageSent.title')}</h3>
                <p className="mb-6 text-neutral-600">
                  {t('contact.messageSent.description')}
                </p>
                <button 
                  onClick={() => {
                    setIsSubmitted(false);
                    setSubmitError('');
                  }}
                  className="text-xs font-bold tracking-widest uppercase text-black underline hover:opacity-70"
                >
                  {t('common.sendAnotherMessage')}
                </button>
              </div>
            ) : (
              <form className="w-full space-y-6" onSubmit={handleSubmit}>
                {/* Honeypot Field (Hidden) */}
                <div className="hidden" aria-hidden="true">
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col space-y-2">
                    <label htmlFor="firstName" className="text-xs font-bold uppercase tracking-widest text-black">
                      {t('common.firstName')} <span className="text-neutral-400 font-normal">({t('common.required')})</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      className="bg-white border border-black/15 rounded-2xl px-5 py-4 text-black placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black transition-all text-sm"
                    />
                  </div>
                  <div className="flex flex-col space-y-2">
                    <label htmlFor="lastName" className="text-xs font-bold uppercase tracking-widest text-black">
                      {t('common.lastName')} <span className="text-neutral-400 font-normal">({t('common.required')})</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      required
                      className="bg-white border border-black/15 rounded-2xl px-5 py-4 text-black placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black transition-all text-sm"
                    />
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  <label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-black">
                    {t('common.email')} <span className="text-neutral-400 font-normal">({t('common.required')})</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="bg-white border border-black/15 rounded-2xl px-5 py-4 text-black placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black transition-all text-sm w-full"
                  />
                </div>

                <div className="flex flex-col space-y-2">
                  <label htmlFor="message" className="text-xs font-bold uppercase tracking-widest text-black">
                    {t('common.message')} <span className="text-neutral-400 font-normal">({t('common.required')})</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    className="bg-white border border-black/15 rounded-2xl px-5 py-4 text-black placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black transition-all text-sm w-full resize-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-black text-white text-xs font-bold tracking-widest px-10 py-4 rounded-full hover:bg-neutral-800 transition-all uppercase shadow-md hover:shadow-lg disabled:opacity-50"
                >
                  {isSubmitting ? t('common.sending') : t('common.send')}
                </button>
                {submitError && (
                  <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">{submitError}</p>
                )}
              </form>
            )}
          </div>
        </div>
      </main>

      <footer className="w-full bg-black text-white px-8 py-10 border-t border-neutral-900">
        <div className="w-full max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-2xl tracking-widest font-bold text-white">FAUS</p>
          <p className="text-xs text-neutral-400 text-center sm:text-left">
            © {new Date().getFullYear()} FAUS. {t('footer.allRightsReserved')}
          </p>
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('footer.linkedinAria')}
            className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors"
          >
            <LinkedInIcon />
          </a>
        </div>
      </footer>

      <TeacherContactModal 
        isOpen={isTeacherModalOpen} 
        onClose={() => setIsTeacherModalOpen(false)} 
        t={t}
        locale={locale}
      />

      {previewVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 md:p-8"
          onClick={() => setPreviewVideo(null)}
        >
          <div
            className="relative w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewVideo(null)}
              className="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors flex items-center gap-2 text-sm font-semibold"
            >
              <span>{t('common.close')}</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="bg-black rounded-2xl overflow-hidden shadow-2xl border border-neutral-800">
              <video
                className="w-full"
                autoPlay
                muted
                loop
                playsInline
                controls
              >
                <source src={previewVideo.src} type="video/mp4" />
              </video>
            </div>
            <div className="mt-4 text-center">
              <h4 className="text-white text-lg font-bold">{previewVideo.title}</h4>
              <p className="text-neutral-400 text-sm mt-1">{previewVideo.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
